import React, { useEffect } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useNavigate,
  useParams,
} from "react-router-dom";

import HomeView from "../views/HomeView/HomeView";
import { Typography } from "@mui/material";
import NewTicketView from "../views/NewTicketView/NewTicketView";
import TicketView from "../views/TicketView/TicketView";
import TicketsView from "../views/TicketsView/TicketsView";
import UserSettingsView from "../views/UserSettingsView/UserSettingsView";
import UserPasswordResetView from "../views/UserPasswordResetView/UserPasswordResetView";
import FinishUserAccountView from "../views/FinishUserAccountView/FinishUserAccountView";
import TicketSuccessView from "../views/TicketSuccessView/TicketSuccessView";
import NotFoundView from "../views/NotFoundView/NotFoundView";
import IntakeView from "../views/IntakeView/IntakeView";
import IntakeReturnView from "../views/IntakeReturnView/IntakeReturnView";
import IntakeCancelledView from "../views/IntakeCancelledView/IntakeCancelledView";
import UserDocumentsView from "../views/UserDocumentsView/UserDocumentsView";
import UserMessagesView from "../views/UserMessagesView/UserMessagesView";
import RouteError from "../components/RouteError/RouteError";
import TextMessagePolicyView from "../views/TextMessagePolicyView/TextMessagePolicyView";

/** Ken's unhyphenated spelling → the canonical `/non-cdl/:tier`. See DefaultRouter for the why. */
const NonCdlAliasRedirect = () => {
  const { tier } = useParams();
  return <Navigate to={`/non-cdl/${tier ?? ""}`} replace />;
};

const AuthRouter = createBrowserRouter(
  [
    {
      /*
       * Public, and registered in every router so it opens whether or not the visitor happens to
       * have a session. The carriers' reviewer has no account at all, and a page that redirected
       * them to a login is what "unverifiable website" means.
       */
      path: "/text-messages",
      element: <TextMessagePolicyView />,
      errorElement: <RouteError />,
    },
    {
      errorElement: <RouteError />,
      path: "/",
      element: <HomeView />,
    },
    {
      errorElement: <RouteError />,
      path: "/home",
      element: <HomeView />,
    },
    {
      errorElement: <RouteError />,
      path: "/new-ticket",
      element: <NewTicketView />,
    },
    {
      errorElement: <RouteError />,
      path: "/tickets",
      element: <TicketsView />,
    },
    {
      errorElement: <RouteError />,
      path: "/ticket",
      element: <TicketView />,
    },
    {
      errorElement: <RouteError />,
      path: "/ticket-success",
      element: <TicketSuccessView />,
    },
    {
      errorElement: <RouteError />,
      path: "/settings",
      element: <UserSettingsView />,
    },
    {
      errorElement: <RouteError />,
      path: "/reset-password",
      element: <UserPasswordResetView />,
    },
    {
      // Account setup link. Registered in all three routers because the link arrives by email and
      // whoever opens it may already have a session; only DefaultRouter had it, so a signed-in
      // visitor got the catch-all 404. The page takes its identity from the payload, not the
      // session, so an existing login changes nothing about what it shows.
      errorElement: <RouteError />,
      path: "/finish-account-creation",
      element: <FinishUserAccountView />,
    },
    {
      // Same as in DefaultRouter: a signed-in visitor must see the same intake page as anyone else,
      // and staff need to be able to open it to check what a client is shown.
      errorElement: <RouteError />,
      path: "/non-cdl/:tier",
      element: <IntakeView product="non-cdl" />,
    },
    {
      // Ken's Aug 5 button map spells it without the hyphen. Redirects onto the canonical path so a
      // signed-in visitor following one of those buttons lands where everyone else does.
      errorElement: <RouteError />,
      path: "/noncdl/:tier",
      element: <NonCdlAliasRedirect />,
    },
    {
      errorElement: <RouteError />,
      path: "/cdl/:tier",
      element: <IntakeView product="cdl" />,
    },
    {
      // Registered here as well as in DefaultRouter, for the same reason the intake page is: an
      // existing client buying a second case is signed in, and the materialiser attaches that case
      // to them rather than making a new client. Their return from Stripe must not hit "*".
      errorElement: <RouteError />,
      path: "/thank-you",
      element: <IntakeReturnView outcome="paid" />,
    },
    {
      errorElement: <RouteError />,
      path: "/received",
      element: <IntakeReturnView outcome="received" />,
    },
    {
      // Same reason as the two above: a signed-in client abandoning checkout on their second case
      // returns here, and must not hit "*".
      errorElement: <RouteError />,
      path: "/cancelled",
      element: <IntakeCancelledView />,
    },
    {
      errorElement: <RouteError />,
      path: "documents",
      element: <UserDocumentsView />,
    },
    {
      errorElement: <RouteError />,
      path: "messages",
      element: <UserMessagesView />,
    },
    {
      errorElement: <RouteError />,
      path: "*",
      element: <NotFoundView />,
    },
  ],
  {}
);
export default AuthRouter;